import { ContactProspect } from "@/pages/Private/pages/Contacts/schema/contacts";
import { People } from "@/pages/Private/pages/Apollo/schema/get-people";

import { EmailProvenance } from "./emailProvenance";

/**
 * Which redux domain owns the prospect being displayed. The unified contact
 * component reads/writes through the matching update + enrichment endpoints.
 * - "contact": the Contacts table (`contact.api`)
 * - "list":    List / Messages surfaces — Unassigned, Inbox, Outbox, ReadyToSend (`list.api`)
 */
export type ContactDomain = "contact" | "list";

/**
 * Normalized, surface-agnostic shape consumed by `ContactChannels`. Every table
 * maps its own row type (`ContactProspect`, `People`, …) into this via the
 * `map*ToContactChannelData` helpers below.
 */
export interface ContactChannelData extends EmailProvenance {
	id: number;
	name: string | null;
	firstName: string | null;
	middleName: string | null;
	lastName: string | null;
	gender: string | null;
	email: string | null;
	emailStatus: string | null;
	phone: string | null;
	phoneStatus: string | null;
	linkedinUrl: string | null;
	bookingLink: string | null;
	/** Unconfirmed email candidates found for the prospect (list/Messages surfaces only). */
	pendingEmails: { id: number; email: string }[];
}

export const mapContactProspectToChannelData = (row: ContactProspect): ContactChannelData => ({
	id: row.id,
	name: row.name ?? null,
	firstName: row.firstName ?? null,
	middleName: row.middleName ?? null,
	lastName: row.lastName ?? null,
	gender: row.gender ?? null,
	email: row.email ?? null,
	emailStatus: row.emailStatus ?? null,
	phone: row.phone ?? null,
	phoneStatus: row.phoneStatus ?? null,
	linkedinUrl: row.linkedinUrl ?? null,
	bookingLink: row.bookingLink ?? null,
	pendingEmails: [],
	...emailProvenanceOf(row, row.emailSetAt),
});

type ListPersonProspect = People & { middleName?: string | null; bookingLink?: string | null };

/**
 * The hover's provenance (PREC-4262). The source, the verification and the last email are the
 * caller's company's facts, so the API sends them beside the shared prospect (on the list row, or
 * top-level on a contact); only when the address was written (`emailSetAt`) is the prospect's.
 */
export const emailProvenanceOf = (
	facts: EmailProvenance | null | undefined,
	emailSetAt: string | Date | null | undefined
): EmailProvenance => ({
	emailSource: facts?.emailSource ?? null,
	emailSetAt: emailSetAt ?? null,
	emailVerifiedAt: facts?.emailVerifiedAt ?? null,
	emailVerificationResult: facts?.emailVerificationResult ?? null,
	lastEmailOutcome: facts?.lastEmailOutcome ?? null,
});

/**
 * On List / Messages surfaces the `id` MUST be the ListPerson id, not the
 * prospect id: `list.api`'s updateProspect (`list/prospect/:id`) and the
 * search endpoints all look the row up by ListPerson id, and the list slice
 * matcher keys off it too. Contact fields are read from the nested prospect.
 */
export const mapListPersonToChannelData = (
	listPerson: {
		id: number;
		prospect?: ListPersonProspect | null;
	} & EmailProvenance
): ContactChannelData => {
	const prospect = listPerson.prospect;

	return {
		id: listPerson.id,
		name: prospect?.name ?? null,
		firstName: prospect?.firstName ?? null,
		middleName: prospect?.middleName ?? null,
		lastName: prospect?.lastName ?? null,
		gender: prospect?.gender ?? null,
		email: prospect?.email ?? null,
		emailStatus: prospect?.emailStatus ?? null,
		phone: prospect?.phone ?? null,
		phoneStatus: prospect?.phoneStatus ?? null,
		linkedinUrl: prospect?.linkedinUrl ?? null,
		bookingLink: prospect?.bookingLink ?? null,
		pendingEmails: (prospect?.pendingEmails ?? []).map((pe) => ({ id: pe.id, email: pe.email })),
		...emailProvenanceOf(listPerson, prospect?.emailSetAt),
	};
};
